import { router } from "@inertiajs/vue3";
import { useDebounceFn } from "@vueuse/core";
import { computed, ref, watch } from "vue";

import { useApiQuery } from "@ja/portal-query";

import { buildCrudUrl } from "../config";
import type {
  GlobalSearchResultItem,
  GlobalSearchResults
} from "../types";

const isOpen = ref(false);
const query = ref("");
const selectedModel = ref<string | null>(null);

// Search Query Logic
const debouncedQuery = ref("");
const updateDebouncedQuery = useDebounceFn((val: string) => {
  debouncedQuery.value = val;
}, 300);

watch(query, (val) => {
  if (!val) {
    debouncedQuery.value = "";
  } else {
    void updateDebouncedQuery(val);
  }
});

/**
 * Open the search dialog.
 */
function open(): void {
  isOpen.value = true;
}

/**
 * Close the search dialog and reset state.
 */
function close(): void {
  isOpen.value = false;
  query.value = "";
  debouncedQuery.value = "";
  selectedModel.value = null;
}

/**
 * Handle result selection - navigate to the result URL.
 */
function selectResult(item: GlobalSearchResultItem): void {
  close();
  router.visit(item.url);
}

// Queries
export function useGlobalSearch() {
  const { data: modelsData, isLoading: isLoadingModels } = useApiQuery<any>(
    ["global-search", "models"],
    buildCrudUrl("global-search/models"),
    {},
    {
      enabled: isOpen,
      staleTime: 5 * 60 * 1000,
    }
  );

  const searchableModels = computed(() => modelsData.value?.models ?? []);

  const queryKey = computed(() => [
    "global-search",
    "results",
    debouncedQuery.value,
    selectedModel.value ?? "__all__",
  ]);

  const { data: resultsData, isLoading, error: queryError } = useApiQuery<GlobalSearchResults>(
    queryKey,
    () => {
      let url = buildCrudUrl(`global-search?q=${encodeURIComponent(debouncedQuery.value)}&limit=5`);
      if (selectedModel.value) {
        url += `&models[]=${encodeURIComponent(selectedModel.value)}`;
      }
      return url;
    },
    {},
    {
      enabled: () => debouncedQuery.value.length > 0,
    }
  );

  const results = computed(() => resultsData.value ?? null);
  const error = computed(() => (queryError.value as Error)?.message ?? null);

  return {
    isOpen,
    query,
    selectedModel,
    results,
    isLoading,
    error,
    searchableModels,
    isLoadingModels,
    open,
    close,
    selectResult,
  };
}
